/**
 * SE-6490 — the pipeline card's tags row (Player Card v7, row 4).
 *
 * @remarks
 * The design shows the player's tags as REMOVABLE chips, each carrying its own
 * `×`, with any tags past the visible row collapsing into a `+N` counter. That
 * is `RemovablePill` plus a muted `+N` — the same pairing `MobileTargetCardView`
 * uses for its watchlist pills, and that component is the registry-mapped
 * implementation of this same Figma component, so the `info-soft` tone here is
 * the one SE-5522 pixel-audited against the design.
 *
 * A viewer who may not edit the board gets the same chip WITHOUT the `×`
 * (a plain `TonePill` on the identical tone), so read-only users see the tags
 * but no affordance that would fail.
 *
 * **Two affordances the design does not have were removed** (SE-6490, resolved
 * 2026-08-04):
 *
 * - the bare, unlabelled `+` add-tag button, and
 * - the `Show More` / `Show Less` toggle, whose job the `+N` counter now does.
 *
 * Tag EDITING is unaffected — pressing the card opens the preview drawer, which
 * is where tags are managed. Only the card-face shortcut is gone.
 * @module components/organisms/custom/ui/PlayerCardTags/PlayerCardTagsView
 */
import { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import RemovableTagChip from "@/components/organisms/custom/ui/RemovableTagChip";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/** A tag as the card face needs it. */
export interface CardTag {
  /** Tag display name — also the removal key. */
  readonly name: string;
  /** Board-scoped colour from GraphQL (not rendered; the tone is tokenized). */
  readonly color?: string;
}

/** Props for PlayerCardTagsView */
interface PlayerCardTagsViewProps {
  /** Tags to render, already filtered and locally reconciled */
  readonly playerTags: CardTag[];
  /** Tags beyond the visible row — renders `+N` when above zero */
  readonly overflowCount: number;
  /** Whether the viewer may remove tags (drives the `×` affordance) */
  readonly showRemoveButtons?: boolean;
  /** Removal handler, called with the tag name */
  readonly onRemoveTag: (tagName: string) => void;
  /** Suppresses a drag starting from a tag interaction */
  readonly onPreventDrag?: () => void;
}

/**
 * Renders the removable tag chips and the overflow counter.
 * @param root0 - Component props
 * @param root0.playerTags - Tags to render
 * @param root0.overflowCount - Tags beyond the visible row
 * @param root0.showRemoveButtons - Whether the `×` affordance renders
 * @param root0.onRemoveTag - Removal handler
 * @param root0.onPreventDrag - Drag suppression on tag interaction
 */
const PlayerCardTagsView = ({
  playerTags,
  overflowCount,
  showRemoveButtons = false,
  onRemoveTag,
  onPreventDrag,
}: PlayerCardTagsViewProps) =>
  playerTags.length === 0 ? null : (
    <Stack
      direction="horizontal"
      className="w-full flex-wrap items-center gap-1"
      testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_TAGS}
    >
      {playerTags.map(tag =>
        showRemoveButtons ? (
          <RemovableTagChip
            key={tag.name}
            name={tag.name}
            onRemoveTag={onRemoveTag}
            onPreventDrag={onPreventDrag}
          />
        ) : (
          <TonePill
            key={tag.name}
            tone="info-soft"
            label={tag.name}
            textVariant="caption"
            className="max-w-40 px-2 py-1"
          />
        )
      )}
      {overflowCount > 0 ? (
        <Text
          variant="micro"
          className="font-medium text-content-muted"
          testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_TAG_OVERFLOW}
        >
          {`+${overflowCount}`}
        </Text>
      ) : null}
    </Stack>
  );

PlayerCardTagsView.displayName = "PlayerCardTagsView";

export default memo(PlayerCardTagsView);
